import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { MoreHorizontal } from 'lucide-react';

export type ToolbarButton =
  | {
      buttonType: 'link';
      id: string;
      label: string;
      href: string;
      icon?: React.ReactNode;
      disabled?: boolean;
      show?: boolean;
      className?: string;
    }
  | {
      buttonType: 'action';
      id: string;
      label: string;
      onClick: () => void;
      icon?: React.ReactNode;
      disabled?: boolean;
      show?: boolean;
      className?: string;
    };

interface ResponsiveToolbarProps {
  items: ToolbarButton[];
  type?: 'image' | 'gif';
  className?: string;
  moreText: string;
}

export default function ResponsiveToolbar({
  items,
  type = 'gif',
  className = '',
  moreText = 'More'
}: ResponsiveToolbarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [visibleButtons, setVisibleButtons] = useState<ToolbarButton[]>([]);
  const [hiddenButtons, setHiddenButtons] = useState<ToolbarButton[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const buttonMinWidth = type === 'image' ? 100 : 120;
  const maxCols = type === 'image' ? 4 : 3;
  const maxRows = type === 'image' ? 1 : 2;
  const gapSize = 8;
  const defaultButtonClass = `h-10 inline-flex min-w-[${buttonMinWidth}px] items-center justify-center gap-2 rounded-md border border-grey-outline bg-grey-button px-4 py-2 whitespace-nowrap w-full hover:brightness-110`;

  const visibleItems = items.filter((b) => b.show !== false);

  const getCols = (containerWidth: number) => {
    let cols = Math.min(maxCols, Math.floor(containerWidth / buttonMinWidth) || 1);
    while (cols > 1 && cols * buttonMinWidth + (cols - 1) * gapSize > containerWidth) {
      cols--;
    }
    return cols;
  };

  const updateLayout = () => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;

    if (type === 'image') {
      const availableWidth = containerWidth - 5;
      const totalWidthAllButtons =
        visibleItems.length * buttonMinWidth + (visibleItems.length - 1) * gapSize;

      if (totalWidthAllButtons <= availableWidth) {
        setVisibleButtons(visibleItems);
        setHiddenButtons([]);
      } else {
        const availableForButtons = availableWidth - (60 + gapSize);
        let visibleCount = 0;
        for (let i = 1; i < visibleItems.length; i++) {
          const width = i * buttonMinWidth + (i - 1) * gapSize;
          if (width <= availableForButtons) visibleCount = i;
          else break;
        }
        visibleCount = Math.max(1, visibleCount);
        setVisibleButtons(visibleItems.slice(0, visibleCount));
        setHiddenButtons(visibleItems.slice(visibleCount));
      }
    } else {
      const cols = getCols(containerWidth);
      const totalSlots = cols * maxRows;
      if (visibleItems.length <= totalSlots) {
        setVisibleButtons(visibleItems);
        setHiddenButtons([]);
      } else {
        setVisibleButtons(visibleItems.slice(0, totalSlots - 1));
        setHiddenButtons(visibleItems.slice(totalSlots - 1));
      }
    }
  };

  useEffect(() => {
    updateLayout();
    const handleResize = () => updateLayout();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [items, type]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderButton = (button: ToolbarButton, isDropdown = false) => {
    const mainClassName = button.className || defaultButtonClass;
    const dropdownClassName = `h-10 inline-flex min-w-[${buttonMinWidth}px] items-center justify-start gap-2 rounded-md whitespace-nowrap w-full hover:bg-[#4D4F59] p-2`;
    const cls = isDropdown ? dropdownClassName : mainClassName;

    const content = (
      <>
        {button.icon && <span className="flex-shrink-0">{button.icon}</span>}
        <span>{button.label}</span>
      </>
    );

    return button.buttonType === 'link' ? (
      <Link key={button.id} href={button.href} className={cls}>
        {content}
      </Link>
    ) : (
      <button key={button.id} onClick={button.onClick} className={cls} disabled={button.disabled}>
        {content}
      </button>
    );
  };

  const getGridLayout = () => {
    if (type === 'image') {
      return {
        gridTemplateColumns: `repeat(${
          visibleButtons.length + (hiddenButtons.length > 0 ? 1 : 0)
        }, 1fr)`,
        gridAutoRows: 'min-content'
      };
    } else {
      const cols = getCols(containerRef.current?.offsetWidth || 360);
      return {
        gridTemplateColumns: `repeat(${cols}, minmax(${buttonMinWidth}px, 1fr))`,
        gridTemplateRows: `repeat(${maxRows}, min-content)`
      };
    }
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className="grid gap-2" style={getGridLayout()}>
        {visibleButtons.map((button) => renderButton(button))}

        {hiddenButtons.length > 0 && (
          <div className="relative w-full" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`${defaultButtonClass} flex h-10 items-center justify-center`}
            >
              <MoreHorizontal size={16} className="flex-shrink-0" />
              {type === 'gif' && <span>{moreText}</span>}
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 top-full z-10 mt-1 rounded-md border border-grey-outline bg-[#202124] p-2 shadow-lg">
                {hiddenButtons.map((button) => (
                  <div key={button.id}>{renderButton(button, true)}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
